import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useCallback,
  useState,
} from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { webSocketEndpoint } from "../environment";
import { getSingleUser } from "../components/api/Individual";
import { getDiscover, getOrganization } from "../components/api/Organization";

const WebSocketContext = createContext(null);

export const WebSocketProvider = (props = {}) => {
  const { children } = props;
  const [lastMessage, setLastMessage] = useState(null);
  const user = useSelector((state) => state.auth.user);
  const location = useLocation();

  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const isConnecting = useRef(false);
  const practitionerData = useRef(null);
  const isCTIEnabledRef = useRef(false);
  // Tracks whether the View Encounter modal is currently open so we can
  // decide whether to keep or drop websocket messages.
  const isEncounterModalOpenRef = useRef(false);
  const practitionerId = user?.["custom:practitioner_id"];
  const token = localStorage.getItem("authentication");
  const orgId = user?.["custom:orgId"];

  const loadPractitionerData = useCallback(async () => {
    try {
      const res = await getSingleUser({
        id: practitionerId,
        type: "Practitioner",
        userOrgId: orgId,
      });
      //const ctiResp = await getDiscover(orgId);
      const ctiResp = await getOrganization(orgId)
      
      isCTIEnabledRef.current = ctiResp?.data?.ctiSettings?.enabled || false;
      practitionerData.current = res.data;
    } catch (err) {
      console.error("Error fetching practitioner data:", err);
    }
  }, [practitionerId, orgId]);

  const disconnect = useCallback(() => {
    if (wsRef.current) {
      wsRef.current.close(1000, "User logout");
      wsRef.current = null;
    }
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    isConnecting.current = false;
  }, []);

  const clearLastMessage = () => setLastMessage(null);

  const scheduleReconnect = useCallback(() => {
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    reconnectTimer.current = setTimeout(() => {
      console.log("Reconnecting WebSocket after TTL");
      disconnect();
      connect();
    }, 1000 * 60 * 8);
  }, [disconnect]);

  const connect = useCallback(async () => {
    if (
      !practitionerId ||
      wsRef.current ||
      isConnecting.current || 
      !token
    ) {
      return;
    }
    isConnecting.current = true;

    await loadPractitionerData();

    if(!isCTIEnabledRef.current) {
        console.log("CTI is not enabled for this organization.");
        return;
    }

    const scopes = JSON.stringify(
      practitionerData.current?.scopeOfPractice || []
    );
    const wsUrl = `wss://${webSocketEndpoint}`
      + `?token=${token}`
      + `&organizationId=${orgId}`
      + `&practitionerId=${practitionerId}`
      + `&scopeOfPractice=${encodeURIComponent(scopes)}`;

    wsRef.current = new WebSocket(wsUrl);

    wsRef.current.onopen = () => {
      console.log("WebSocket connected");
      scheduleReconnect();
      isConnecting.current = false;
    };

    wsRef.current.onmessage = (evt) => {
      try {
        const msg = JSON.parse(evt.data);
        // If this is an OUTBOUND CTI message and the View Encounter modal
        // is not currently open, drop it so it doesn't get queued up and
        // later incorrectly attached to a different encounter.
        if (msg?.callType === "OUTBOUND" && !isEncounterModalOpenRef.current) {
          return;
        }

        setLastMessage(msg);
      } catch (e) {
        console.error("WS parse error", e);
      }
    };

    wsRef.current.onerror = () => {
      isConnecting.current = false;
    };

    wsRef.current.onclose = () => {
      console.log("WebSocket closed");
      wsRef.current = null;
      isConnecting.current = false;
    };
  }, [practitionerId, orgId, token, loadPractitionerData, scheduleReconnect]);

  useEffect(() => {
    const excluded = ["interaction", "zoom-meeting-create"];
    const onExcludedPage = excluded.some(seg => location.pathname.includes(seg));
    if ( practitionerId && !onExcludedPage) {
        connect();
    }
    window.addEventListener("beforeunload", disconnect);
    return () => {
      window.removeEventListener("beforeunload", disconnect);
      disconnect();
    };
  }, [practitionerId, connect, disconnect]);

  useEffect(() => {
        const excludedSegments = ["interaction", "zoom-meeting-create"];

        const isExcluded = excludedSegments.some((seg) => location.pathname.includes(seg));

        if (isExcluded) {
            disconnect();
        } else if (practitionerId && !wsRef.current) {
            connect();
        }
    }, [location.pathname, practitionerId, connect, disconnect]);

  const setEncounterModalOpen = useCallback((isOpen) => {
    isEncounterModalOpenRef.current = !!isOpen;
  }, []);

  return (
    <WebSocketContext.Provider
      value={{
        socket: wsRef.current,
        connect,
        disconnect,
        lastMessage,
        clearLastMessage,
        setEncounterModalOpen,
      }}
    >
      {children}
    </WebSocketContext.Provider>
  );
};

export const useWebSocket = () => useContext(WebSocketContext);